import Airtable from "airtable";
import { useEffect, useMemo, useState } from "react";
import "./App.css";
import notify from "./discord";

// Format time difference with 'Generated' prefix and short units
const timeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  let interval = Math.floor(seconds / 31536000);
  if (interval >= 1) return `Generated ${interval}y ago`;

  interval = Math.floor(seconds / 2592000);
  if (interval >= 1) return `Generated ${interval}mo ago`;

  interval = Math.floor(seconds / 86400);
  if (interval >= 1) return `Generated ${interval}d ago`;

  interval = Math.floor(seconds / 3600);
  if (interval >= 1) return `Generated ${interval}h ago`;

  interval = Math.floor(seconds / 60);
  if (interval >= 1) return `Generated ${interval}m ago`;

  return "Generated just now";
};

// Fisher-Yates shuffle algorithm
const shuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

// Initialize Airtable
const base = new Airtable({
  apiKey: process.env.REACT_APP_AIRTABLE_API_KEY,
}).base(process.env.REACT_APP_AIRTABLE_BASE_ID);

// Level configuration with colors
const LEVELS = [
  { id: "A1.1", name: "A1.1", color: "#D1E2FF" },
  { id: "A1.2", name: "A1.2", color: "#C4ECFF" },
  { id: "A2.1", name: "A2.1", color: "#C1F5F0" },
  { id: "A2.2", name: "A2.2", color: "#CFF5D1" },
];

const CACHE_KEY = "quiz_deutsch_cache";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export default function App() {
  // State declarations
  const [quizStarted, setQuizStarted] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentLevel, setCurrentLevel] = useState("");
  const [cachedData, setCachedData] = useState(null);

  // Memoized values
  const question = questions[currentIndex];
  const currentLevelData = useMemo(() => LEVELS.find((level) => level.id === currentLevel) || {}, [currentLevel]);
  // We don't need to shuffle here anymore as options are already shuffled in loadQuestions
  const options = question?.options || [];

  // Fetch all questions from Airtable
  const fetchAllQuestions = async () => {
    try {
      const allData = {};

      for (const level of LEVELS) {
        const records = await base("Questions")
          .select({
            filterByFormula: `{level} = '${level.id}'`,
            maxRecords: 1,
          })
          .firstPage();

        if (records.length > 0) {
          const record = records[0];
          allData[level.id] = {
            questions: JSON.parse(record.fields.questions),
            lastUpdated: record.fields.last_updated,
          };
        }
      }

      const cacheData = {
        data: allData,
        timestamp: Date.now(),
      };

      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      setCachedData(cacheData);
      return cacheData;
    } catch (err) {
      console.error("Error fetching questions:", err);
      throw err;
    }
  };

  // Load questions on mount
  useEffect(() => {
    const initializeQuestions = async () => {
      try {
        setLoading(true);
        setError("");

        // Check localStorage for cached data
        const cached = localStorage.getItem(CACHE_KEY);

        if (cached) {
          const parsedCache = JSON.parse(cached);
          const now = Date.now();
          const timeDiff = now - parsedCache.timestamp;

          // If cache is less than 24 hours old, use it
          if (timeDiff < CACHE_DURATION) {
            setCachedData(parsedCache);
            setLoading(false);
            return;
          }
        }

        // Cache is old or doesn't exist, fetch fresh data
        await fetchAllQuestions();
      } catch (err) {
        setError("Failed to load questions. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    };

    initializeQuestions();
  }, []);

  // Load questions for a specific level from cache, shuffle them, and select 10 random questions
  const loadQuestions = (level) => {
    if (!cachedData || !cachedData.data[level]) {
      setError(`No questions found for level ${level}`);
      return;
    }

    const levelData = cachedData.data[level];
    // Shuffle all questions first
    const shuffledQuestions = shuffleArray([...levelData.questions]);
    // Take first 10 questions
    const selectedQuestions = shuffledQuestions.slice(0, 10);
    // Shuffle options for each selected question
    const questionsWithShuffledOptions = selectedQuestions.map((question) => ({
      ...question,
      options: shuffleArray([...question.options]),
    }));

    setQuestions(questionsWithShuffledOptions);
    setCurrentLevel(level);
    setQuizStarted(true);
    setCurrentIndex(0);
    setCorrectCount(0);
    setSelectedOption(null);
    setShowExplanation(false);
  };

  function selectOption(option) {
    if (selectedOption !== null) return;

    setSelectedOption(option);
    setShowExplanation(true);

    const currentQuestion = questions[currentIndex];
    const isCorrect = option === currentQuestion.answer;

    if (isCorrect) {
      setCorrectCount((c) => c + 1);
    }
  }

  function nextQuestion() {
    setSelectedOption(null);
    setShowExplanation(false);
    setCurrentIndex((i) => i + 1);
  }

  // Send Discord notification when quiz is completed
  useEffect(() => {
    if (currentIndex >= questions.length && questions.length > 0 && quizStarted) {
      const scorePercent = Math.round((correctCount / questions.length) * 100);
      notify("Quiz Completed", {
        level: currentLevel,
        score: `${correctCount}/${questions.length}`,
        percentage: `${scorePercent}%`,
      });
    }
  }, [currentIndex, questions.length, correctCount, currentLevel, quizStarted]);

  if (!quizStarted) {
    if (loading) {
      return (
        <div className="container">
          <div className="loading-spinner"></div>
          <p>Loading questions...</p>
        </div>
      );
    }

    return (
      <div className="container">
        <h1>quiz-deutsch</h1>
        <p className="subtitle">Select your level to begin</p>

        <div className="level-buttons">
          {LEVELS.map((level) => {
            const levelData = cachedData?.data?.[level.id];
            const lastUpdated = levelData?.lastUpdated ? timeAgo(levelData.lastUpdated) : "Unknown";

            return (
              <div key={level.id} className="level-button-container">
                <button onClick={() => loadQuestions(level.id)} className="level-button" style={{ "--level-color": level.color }}>
                  {level.name}
                </button>
                <span
                  className="level-updated"
                  title={`Last updated: ${levelData?.lastUpdated ? new Date(levelData.lastUpdated).toLocaleString() : "Unknown"}`}
                >
                  {lastUpdated}
                </span>
              </div>
            );
          })}
        </div>

        {error && <p className="error-message">{error}</p>}
      </div>
    );
  }

  if (currentIndex >= questions.length && questions.length > 0) {
    const scorePercent = Math.round((correctCount / questions.length) * 100);
    const currentLevelData = LEVELS.find((level) => level.id === currentLevel) || {};

    return (
      <div className="container">
        <h1>Quiz Complete!</h1>
        <div className="score-container" style={{ "--level-color": currentLevelData.color || "#4F46E5" }}>
          <div className="score-percentage">{scorePercent}%</div>
          <div className="score-detail">
            {correctCount} out of {questions.length} correct
          </div>
        </div>
        <button
          onClick={() => {
            setQuizStarted(false);
            setSelectedOption(null);
            setShowExplanation(false);
          }}
          className="btn"
          style={{ backgroundColor: currentLevelData.color || "#4F46E5", marginTop: "2rem" }}
        >
          Choose Another Level
        </button>
      </div>
    );
  }

  return (
    <div className="container">
      <button
        className="back-button"
        onClick={() => {
          setQuizStarted(false);
          setCurrentIndex(0);
          setSelectedOption(null);
          setShowExplanation(false);
        }}
        aria-label="Back to home"
      >
        ← Back
      </button>

      <div className="progress-container">
        <div className="level-indicator" style={{ backgroundColor: currentLevelData.color || "#4F46E5" }}>
          {currentLevel}
        </div>
        <div className="question-counter">
          Question {currentIndex + 1} of {questions.length}
        </div>
      </div>

      <div className="question-container">
        <p className="question">{question.question}</p>

        <div className="options-grid">
          {options.map((option) => {
            let className = "option";
            if (showExplanation) {
              if (option === question.answer) className += " correct";
              else if (option === selectedOption) className += " wrong";
            }
            return (
              <button key={option} className={className} onClick={() => selectOption(option)} disabled={selectedOption !== null}>
                {option}
              </button>
            );
          })}
        </div>

        {showExplanation && (
          <div className="explanation">
            <div className="explanation-label">Explanation:</div>
            <div className="explanation-text">{question.explanation}</div>
            <button onClick={nextQuestion} className="btn next" style={{ backgroundColor: currentLevelData.color || "#4F46E5" }}>
              {currentIndex < questions.length - 1 ? "Next Question" : "See Results"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
